import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import {
  ChangeEvent,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { validateHelpBundle } from "./file_validation";
import type { HelpArticleMetadata } from "./publish_workflow";
import { SafeMarkdown } from "./safe_markdown";

type HelpPublisherViewProps = {
  onPublish: (
    metadata: HelpArticleMetadata,
    files: File[],
    onProgress: (completed: number, total: number) => void,
  ) => Promise<unknown>;
  onCancel: () => void;
};

const slugify = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");

const readTextFile = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });

const fileSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const HelpPublisherView = ({
  onPublish,
  onCancel,
}: HelpPublisherViewProps) => {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [summary, setSummary] = useState("");
  const [category, setCategory] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [markdown, setMarkdown] = useState("");
  const [bundleTouched, setBundleTouched] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const validation = useMemo(() => validateHelpBundle(files), [files]);

  useEffect(() => {
    if (!slugEdited) setSlug(slugify(title));
  }, [slugEdited, title]);

  const applyFiles = async (incoming: File[]) => {
    setBundleTouched(true);
    setPublishError(null);
    setFiles(incoming);
    const nextValidation = validateHelpBundle(incoming);
    if (nextValidation.markdownFile) {
      try {
        setMarkdown(await readTextFile(nextValidation.markdownFile));
      } catch (error) {
        setMarkdown("");
        setPublishError(
          error instanceof Error
            ? error.message
            : "Could not read the Markdown file.",
        );
      }
    } else {
      setMarkdown("");
    }
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    void applyFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void applyFiles(Array.from(event.dataTransfer.files));
  };

  const slugReserved = slug === "publish";
  const slugValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && !slugReserved;
  const metadataValid =
    title.trim().length > 0 &&
    title.trim().length <= 120 &&
    summary.trim().length > 0 &&
    summary.trim().length <= 240 &&
    category.trim().length > 0 &&
    category.trim().length <= 60 &&
    slugValid;
  const ready =
    metadataValid &&
    files.length > 0 &&
    validation.errors.length === 0 &&
    markdown.length > 0 &&
    !publishing;

  const publish = async () => {
    setConfirmOpen(false);
    setPublishing(true);
    setPublishError(null);
    setProgress({ completed: 0, total: files.length });
    try {
      await onPublish(
        {
          title: title.trim(),
          slug,
          summary: summary.trim(),
          category: category.trim(),
        },
        files,
        (completed, total) => setProgress({ completed, total }),
      );
    } catch (error) {
      setPublishError(
        error instanceof Error
          ? error.message
          : "The article could not be published. Try again.",
      );
      setPublishing(false);
    }
  };

  return (
    <Box
      data-testid="help-publisher"
      sx={{
        flex: 1,
        overflowY: "auto",
        bgcolor: "atomic.surfaceTint",
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1320, mx: "auto" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton aria-label="Back to Help center" onClick={onCancel}>
              <ArrowBackRoundedIcon />
            </IconButton>
            <Box>
              <Typography component="h1" variant="h5" fontWeight={700}>
                Publish a Help article
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload one Markdown (.md) file.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<PublishRoundedIcon />}
            disabled={!ready}
            onClick={() => setConfirmOpen(true)}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            Review and publish
          </Button>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(360px, .8fr) minmax(460px, 1.2fr)",
            },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}
            >
              <Typography variant="h6" fontWeight={700} mb={0.5}>
                Article details
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                These details help readers find the right guide quickly.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  fullWidth
                  inputProps={{ maxLength: 120, "aria-label": "Title" }}
                />
                <TextField
                  label="Article slug"
                  value={slug}
                  onChange={(event) => {
                    setSlugEdited(true);
                    setSlug(slugify(event.target.value));
                  }}
                  required
                  fullWidth
                  error={slug.length > 0 && !slugValid}
                  helperText={
                    slugReserved
                      ? "This slug is reserved by the Help center."
                      : "Used in the article URL. Lowercase letters, numbers, and hyphens only."
                  }
                  inputProps={{ maxLength: 80, "aria-label": "Article slug" }}
                />
                <TextField
                  label="Summary"
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                  required
                  multiline
                  minRows={2}
                  fullWidth
                  helperText={`${summary.length}/240`}
                  inputProps={{ maxLength: 240, "aria-label": "Summary" }}
                />
                <TextField
                  label="Category"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                  required
                  fullWidth
                  placeholder="For example, Basics or Forecasting"
                  inputProps={{ maxLength: 60, "aria-label": "Category" }}
                />
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}
            >
              <Typography variant="h6" fontWeight={700} mb={0.5}>
                Markdown file
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Include exactly one .md file. Images and videos are not
                supported.
              </Typography>
              <Box
                role="button"
                tabIndex={0}
                aria-label="Choose or drop a Markdown article file"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                sx={{
                  display: "block",
                  p: 3,
                  border: "1.5px dashed",
                  borderColor: validation.errors.length
                    ? "error.main"
                    : "atomic.faint",
                  borderRadius: 2.5,
                  bgcolor: "atomic.surfaceTint",
                  textAlign: "center",
                  cursor: "pointer",
                  transition:
                    "border-color .15s ease, background-color .15s ease",
                  "&:hover, &:focus-within": {
                    borderColor: "atomic.brandBlue",
                    bgcolor: "atomic.blue1",
                  },
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  aria-label="Markdown article file"
                  accept=".md"
                  onClick={(event) => event.stopPropagation()}
                  onChange={handleFileInput}
                />
                <CloudUploadRoundedIcon
                  sx={{ fontSize: 38, color: "atomic.brandBlue" }}
                />
                <Typography fontWeight={700} mt={0.75}>
                  Drop a Markdown file here or choose a file
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  .md only, 2 MB maximum
                </Typography>
              </Box>

              {files.length > 0 && (
                <Stack spacing={1} mt={2}>
                  {files.map((file) => (
                    <Stack
                      key={file.name}
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: "atomic.surfaceTint",
                      }}
                    >
                      <DescriptionOutlinedIcon color="action" />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fileSize(file.size)}
                        </Typography>
                      </Box>
                      <CheckCircleRoundedIcon
                        fontSize="small"
                        color="success"
                      />
                    </Stack>
                  ))}
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={() => void applyFiles([])}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Clear file
                  </Button>
                </Stack>
              )}

              {bundleTouched && validation.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                    {validation.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </Stack>
                </Alert>
              )}
            </Paper>

            {publishError && (
              <Alert severity="error">
                {publishError} Your Markdown file and article details are still
                here, so you can try again.
              </Alert>
            )}
            {publishing && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {progress.completed < progress.total
                      ? "Uploading Markdown file"
                      : "Validating and publishing"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress.completed}/{progress.total}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant={progress.total ? "determinate" : "indeterminate"}
                  value={
                    progress.total
                      ? (progress.completed / progress.total) * 100
                      : 0
                  }
                />
              </Paper>
            )}
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              position: { lg: "sticky" },
              top: { lg: 16 },
              minHeight: 520,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              px={2.5}
              py={1.5}
              bgcolor="atomic.panelTint"
            >
              <Typography fontWeight={700}>Live preview</Typography>
              <Chip label="Reader view" size="small" variant="outlined" />
            </Stack>
            <Divider />
            <Box
              sx={{
                p: { xs: 2.5, sm: 4 },
                maxHeight: { lg: "calc(100vh - 210px)" },
                overflowY: "auto",
              }}
            >
              {markdown ? (
                <>
                  {category && (
                    <Chip label={category} size="small" sx={{ mb: 2 }} />
                  )}
                  <SafeMarkdown markdown={markdown} assets={[]} />
                </>
              ) : (
                <Box
                  sx={{ py: 12, textAlign: "center", color: "text.secondary" }}
                >
                  <DescriptionOutlinedIcon
                    sx={{ fontSize: 42, color: "atomic.faint" }}
                  />
                  <Typography fontWeight={600} mt={1}>
                    Your article preview will appear here
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    Add a valid Markdown file to preview it before publishing.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            position: "sticky",
            bottom: 0,
            mt: 2,
            py: 1.5,
            bgcolor: "atomic.surfaceTint",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<PublishRoundedIcon />}
            disabled={!ready}
            onClick={() => setConfirmOpen(true)}
          >
            Review and publish
          </Button>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Publish this article now?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This will make <strong>{title}</strong> immediately visible to
            readers in this customer account. If <strong>/{slug}</strong>{" "}
            already exists, this version will replace the currently published
            version.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Other customers cannot see this article or its file.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)}>Keep editing</Button>
          <Button variant="contained" onClick={() => void publish()} autoFocus>
            Publish now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
